import React, { useCallback, useContext, useEffect, useState } from 'react';
import { useLocation } from 'react-router-dom';
import styled, { css } from 'styled-components';
import { layout, media } from '@prisjakt/ui-base';
import { DealsContext } from '../deals-context';
import { ProductListCard } from 'src/components/product-card-new';
import { TooltipAlign } from 'src/components/tooltip-new';
import AddAlert from 'src/components/action-buttons/alert';
import { AddToListButton } from 'src/components/action-buttons/add-to-list';
import { Ads } from '../../ads';
import HighlightedSearchResult from '../highlighted-search-result';
import { useIsAuthenticated } from 'src/hooks/use-is-authenticated';
import { isRaw } from '../../../utils/products';
import { ActiveCampaignByProductId } from 'src/hooks/active-campaign-by-product-id';
import { LayoutMode } from 'src/components/layout-buttons/types';
import { ProductComparisonProps } from '../types';
import { Product, Raw, Offer } from 'src/types';
import { AdsLayoutConfig } from '../../ads/types';

import { useListCardClickoutExperiment } from 'src/hooks/experiments';
import {
  InternalNavigationScope,
  trackInternalNavigationClick,
} from 'src/globals/tracking/tracking';

type ProductCardWrapperProps = {
  onClick?: (product: {
    id: string | number;
    name?: string;
    type?: string;
  }) => void;
  order: number;
  product: Product | Raw | Offer;
  productsWithActiveCampaign?: ActiveCampaignByProductId;
} & ProductComparisonProps;

const ProductCardWrapper = (props: ProductCardWrapperProps) => {
  const {
    deselectProductById,
    isSelectingProducts,
    onClick,
    order,
    product,
    productsWithActiveCampaign,
    selectedProducts,
    selectProductById,
  } = props;

  const { getDealDiffPercentage } = useContext(DealsContext);

  let id!: number;

  if (!isRaw(product)) {
    id = product.id;
  }

  const dealsDiffPercentage = productsWithActiveCampaign
    ? productsWithActiveCampaign[id]?.diffPercentage
    : getDealDiffPercentage(id);

  const isAuthenticated = useIsAuthenticated();

  const onSelect = (productId: number) =>
    selectProductById && selectProductById(productId, 'productCard');

  const onDeselect = (productId: number) =>
    deselectProductById && deselectProductById(productId, 'productCard');

  const onClickHandler = useCallback(
    () =>
      onClick && onClick({ id, name: product.name, type: product.__typename }),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [id]
  );

  const getPrice = () => {
    switch (product.__typename) {
      case 'Product':
        return product.priceSummary.regular;
      case 'Offer':
        return product.offerPrice.regular;
      case 'Raw':
        return product.price;
      default:
        return undefined;
    }
  };

  const disableUnfeaturedClickout = isRaw(product) && !product.store?.featured;

  const location = useLocation();
  const clickoutExperiment = useListCardClickoutExperiment();
  const [isSearchPage, setIsSearchPage] = useState(false);
  const atSearchPage = location.pathname.indexOf('/search') === 0;
  const hasClickOutOnSearchPage =
    clickoutExperiment === 'clickout and list default' && isSearchPage;

  // Just checking the location (atSearchPage) causes the alert button to flash
  // if the card is clicked. Using the check below somehow solves this, maybe somehow related
  // to the routing.
  useEffect(() => {
    if (atSearchPage) {
      setIsSearchPage(true);
    }
  }, []);

  // Related to isClickoutExperiment
  const renderActionArea = (p: Product) => {
    return (
      <>
        {isAuthenticated && (
          <AddToListWrapper $visible={!hasClickOutOnSearchPage}>
            <AddToListButton
              productId={p.id}
              productName={p.name}
              tooltipAlign={TooltipAlign.LEFT}
              dropdownGrow="right"
            />{' '}
          </AddToListWrapper>
        )}
        <AddAlertWrapper $visible={!hasClickOutOnSearchPage}>
          <AddAlert product={p} layout="button" />
        </AddAlertWrapper>
      </>
    );
  };

  return !isRaw(product) ? (
    <ProductCard
      key={product.id}
      container="li"
      id={product.id}
      brand={product.brand}
      highlightedName={
        product.highlight ? (
          <HighlightedSearchResult>{product.highlight}</HighlightedSearchResult>
        ) : (
          undefined
        )
      }
      imageSrc={product.media.first}
      imageSize={100}
      shouldLazyLoadImage
      name={product.name}
      link={product.pathName}
      onClick={onClickHandler}
      properties={product.properties?.nodes}
      price={product.priceSummary.regular}
      priceInclShipping={product.priceSummary.includeShipping}
      rank={product.popularity?.inCategory}
      rating={product.userReviewSummary.rating}
      reviewsCount={product.userReviewSummary.count}
      selectable={isSelectingProducts}
      selected={Boolean(selectedProducts?.find(p => p.id === product.id))}
      diffPercentage={dealsDiffPercentage}
      priceCount={product.priceSummary.count}
      ActionArea={renderActionArea(product)}
      onSelect={() => onSelect(product.id)}
      onDeselect={() => onDeselect(product.id)}
      $order={order}
      showClickout={
        clickoutExperiment === 'clickout and list default' && isSearchPage
      }
      cheapestOffer={product.cheapestOffer}
    />
  ) : (
    <OfferCard
      type="offer"
      key={`${product.name}-${order}`}
      name={product.name}
      onClick={onClickHandler}
      price={getPrice()}
      store={product.store}
      link={disableUnfeaturedClickout ? undefined : product.externalUri}
      $order={order}
      imageSrc={
        product.__typename === 'Offer' ? product.media?.first : undefined
      }
    />
  );
};

type Props = {
  feedbackPrompt?: React.ReactNode | null;
  feedbackPromptPosition?: number;
  onClick?: (productId: string | number, name?: string, type?: string) => void;
  productsWithActiveCampaign?: ActiveCampaignByProductId;
  products: (Product | Raw)[];
  adslayoutConfig?: AdsLayoutConfig;
  embedPanoramaAd?: boolean;
  hasFeedback?: boolean;
} & ProductComparisonProps;

export const ListLayout = (props: Props) => {
  const {
    feedbackPrompt,
    feedbackPromptPosition = 5,
    onClick,
    productsWithActiveCampaign,
    products,
    adslayoutConfig,
    embedPanoramaAd,
    hasFeedback,
    ...productComparisonProps
  } = props;

  return (
    <List>
      {products.map((product, index) => (
        <ProductCardWrapper
          key={
            !isRaw(product) ? product.id : `${product.name}-${index.toString()}`
          }
          onClick={(productData: {
            id: string | number;
            name?: string;
            type?: string;
          }) => {
            onClick?.(productData.id, productData.name, productData.type);
            if (!isRaw(product))
              trackInternalNavigationClick({
                scope: InternalNavigationScope.LIST_LAYOUT_PRODUCT_CARD,
                nextPage: product.pathName,
                index,
              });
          }}
          order={index + 1}
          product={product}
          productsWithActiveCampaign={productsWithActiveCampaign}
          {...productComparisonProps}
        />
      ))}

      <Ads
        offersLength={products.length}
        layout={LayoutMode.LIST}
        layoutConfig={adslayoutConfig}
      />

      {hasFeedback && (
        <li style={{ order: feedbackPromptPosition - 1 }}>{feedbackPrompt}</li>
      )}
    </List>
  );
};

const List = styled.ul`
  display: grid;
  grid-template-rows: repeat(auto-fill, 1fr);
  gap: ${layout.measurements.spacing100};
  margin-bottom: ${layout.measurements.spacing100};
  list-style: none;
`;

const ProductCard = styled(ProductListCard)<{ $order: number }>`
  order: ${p => p.$order};
`;

const OfferCard = styled(ProductListCard)<{ $order: number }>`
  order: ${p => p.$order};
`;

const AddAlertWrapper = styled.div<{ $visible?: boolean }>`
  position: absolute;
  top: ${layout.measurements.spacing100};
  right: ${layout.measurements.spacing100};

  ${media.mediumDown`
    display:none;
  `};

  ${p =>
    !p.$visible &&
    css`
      display: none;
    `}
`;

const AddToListWrapper = styled.div<{ $visible?: boolean }>`
  ${p =>
    !p.$visible &&
    css`
      display: none;
    `}
  position: absolute;
  top: ${layout.measurements.spacing100};
  left: ${layout.measurements.spacing100};
`;
